import { currentEmbeddedApp } from "@/lib/app-embed/readEmbeddedApp";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { cpf, cnpj } from "cpf-cnpj-validator";
import { ArrowLeft, CheckCircle, Clock, Copy, CreditCard, QrCode } from "lucide-react";
import Layout from "@/components/Layout";
import { CoursePurchasePhoneField } from "@/components/courses/CoursePurchasePhoneField";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  useGetCourseBySlugQuery,
  useMyDataQuery,
  useCoursePurchaseStatusLazyQuery,
  useCreateCoursePurchaseCheckoutMutation,
  useCreateCoursePurchasePixMutation,
} from "@/generated/graphql";
import { VALIDATE_COURSE_COUPON } from "@/queries/courses";
import { deriveCoursePurchaseState } from "@/lib/courses/deriveCoursePurchaseState";
import {
  deriveCoursePurchasePhoneInitialValue,
  resolveCoursePurchasePhone,
} from "@/lib/courses/coursePurchasePhone";
import { formatPixCountdown } from "@/lib/billing/formatPixCountdown";
import {
  clearRememberedViralizePurchase,
  rememberViralizePurchase,
  trackViralizeCheckout,
  trackViralizePurchase,
} from "@/lib/analytics/trackViralizePurchase";

const POLL_INTERVAL_MS = 5000;

function formatCoursePrice(price: number): string {
  return `R$ ${price.toFixed(2).replace(".", ",")}`;
}

interface PixOrderState {
  purchase_id: string;
  pix_code: string;
  pix_qr_code_url: string;
  expires_at: string;
  value: number;
}

const CourseCheckoutContent = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en" : "pt-BR";
  const navigate = useNavigate();

  const { data, loading: courseLoading } = useGetCourseBySlugQuery({
    variables: { slug: slug ?? "", locale },
    skip: !slug,
    fetchPolicy: "network-only",
  });
  const course = data?.course ?? null;

  const { data: myData, loading: myDataLoading } = useMyDataQuery();

  const [paymentMethod, setPaymentMethod] = useState<"pix" | "credit_card">("credit_card");
  const [documentType, setDocumentType] = useState<"cpf" | "cnpj">("cpf");
  const [document, setDocument] = useState("");
  const [phoneDraft, setPhoneDraft] = useState(() => deriveCoursePurchasePhoneInitialValue());
  const { country: phoneCountry, phone } = phoneDraft;
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount: number;
    amount: number;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pixOrder, setPixOrder] = useState<PixOrderState | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const phoneInitializedRef = useRef(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [createCoursePurchasePix] = useCreateCoursePurchasePixMutation();
  const [createCoursePurchaseCheckout] = useCreateCoursePurchaseCheckoutMutation();
  const [fetchPurchaseStatus] = useCoursePurchaseStatusLazyQuery({ fetchPolicy: "network-only" });
  const [validateCourseCoupon, { loading: couponValidating }] = useLazyQuery(VALIDATE_COURSE_COUPON, {
    fetchPolicy: "network-only",
  });
  const couponFromUrlRef = useRef<string | null>(null);

  const profilePhone = myData?.myData?.phone_number ?? "";

  useEffect(() => {
    const couponParam = searchParams.get("coupon");
    if (couponParam) {
      const couponUpper = couponParam.trim().toUpperCase();
      setCouponCode(couponUpper);
      couponFromUrlRef.current = couponUpper;
    }
  }, [searchParams]);

  useEffect(() => {
    if (myDataLoading || phoneInitializedRef.current) return;
    phoneInitializedRef.current = true;
    setPhoneDraft(deriveCoursePurchasePhoneInitialValue(profilePhone));
  }, [myDataLoading, profilePhone]);

  // Returning to checkout means a previous card redirect was canceled or
  // abandoned, so its marker must never become a later false conversion.
  useEffect(() => {
    clearRememberedViralizePurchase();
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    };
  }, []);

  // 1s countdown tick while a PIX order is awaiting payment.
  useEffect(() => {
    if (pixOrder?.expires_at) {
      setCurrentTime(new Date());
      timeUpdateIntervalRef.current = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
    } else if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    };
  }, [pixOrder]);

  const clearAllIntervals = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  };

  const couponErrorMessage = (error?: string | null) => {
    switch (error) {
      case "coupon_expired":
        return t("coursePurchase.couponExpired");
      case "coupon_exhausted":
        return t("coursePurchase.couponExhausted");
      case "coupon_not_valid_for_course":
        return t("coursePurchase.couponNotValid");
      case "coupon_not_found":
      case "invalid_course_coupon":
        return t("coursePurchase.couponInvalid");
      default:
        return t("checkout.couponValidationErrorDesc");
    }
  };

  const applyCoupon = async (code: string) => {
    if (!slug || !code.trim()) {
      toast({
        title: t("checkout.couponRequired"),
        description: t("checkout.enterCouponCodeRequired"),
        variant: "destructive",
      });
      return;
    }
    try {
      const { data: couponData } = await validateCourseCoupon({
        variables: { code: code.trim(), slug },
      });
      const preview = couponData?.validateCourseCoupon;
      if (preview?.valid && preview.code && preview.amount != null && preview.discount != null) {
        setAppliedCoupon({
          code: preview.code,
          discount: preview.discount,
          amount: preview.amount,
        });
        setCouponCode(preview.code);
        toast({
          title: t("checkout.couponApplied", { code: preview.code }),
          description: t("checkout.discount", { percent: preview.discount }),
        });
        return;
      }
      setAppliedCoupon(null);
      toast({
        title: t("checkout.couponInvalid"),
        description: couponErrorMessage(preview?.error),
        variant: "destructive",
      });
    } catch {
      setAppliedCoupon(null);
      toast({
        title: t("checkout.couponValidationError"),
        description: t("checkout.couponValidationErrorDesc"),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (!slug || !couponFromUrlRef.current || couponCode !== couponFromUrlRef.current) return;
    const code = couponFromUrlRef.current;
    couponFromUrlRef.current = null;
    void applyCoupon(code);
    // Auto-apply only once from the URL coupon, after the course slug is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, couponCode]);

  const handleMutationError = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error ?? "");
    if (message.includes("already_owned")) {
      toast({ title: t("coursePurchase.alreadyOwned") });
      navigate(`/courses/${slug}`);
      return;
    }
    if (
      message.includes("coupon_not_found")
      || message.includes("coupon_expired")
      || message.includes("coupon_exhausted")
      || message.includes("coupon_not_valid_for_course")
      || message.includes("invalid_course_coupon")
    ) {
      toast({
        title: t("checkout.couponInvalid"),
        description: couponErrorMessage(message),
        variant: "destructive",
      });
      return;
    }
    toast({
      title: t("coursePurchase.purchaseError"),
      variant: "destructive",
    });
  };

  const checkPurchaseStatus = async (purchaseId: string, value: number) => {
    try {
      const { data: statusData } = await fetchPurchaseStatus({ variables: { purchaseId } });
      const status = statusData?.coursePurchaseStatus?.status;

      if (status === "paid") {
        clearAllIntervals();
        trackViralizePurchase({
          transactionId: purchaseId,
          value,
        });
        toast({
          title: t("coursePurchase.confirmedTitle"),
          description: t("coursePurchase.confirmedBody"),
        });
        navigate(`/courses/${slug}?purchase=success`);
        return;
      }

      if (status === "failed") {
        clearAllIntervals();
        toast({
          title: t("coursePurchase.purchaseError"),
          variant: "destructive",
        });
        setPixOrder(null);
      }
      // Any other status (e.g. pending) keeps polling.
    } catch (error) {
      console.error("Error checking course purchase status:", error);
    }
  };

  const startPolling = (purchaseId: string, value: number) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    checkPurchaseStatus(purchaseId, value);
    pollingIntervalRef.current = setInterval(() => {
      checkPurchaseStatus(purchaseId, value);
    }, POLL_INTERVAL_MS);
  };

  const formatDocument = (value: string, type: "cpf" | "cnpj") => {
    const cleaned = value.replace(/\D/g, "");
    return type === "cpf" ? cpf.format(cleaned) : cnpj.format(cleaned);
  };

  const validateDocument = (value: string, type: "cpf" | "cnpj") => {
    const cleaned = value.replace(/\D/g, "");
    return type === "cpf" ? cpf.isValid(cleaned) : cnpj.isValid(cleaned);
  };

  const handleDocumentChange = (value: string) => {
    setDocument(formatDocument(value, documentType));
  };

  const copyPixCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast({
        title: t("checkout.pixCodeCopied"),
        description: t("checkout.pixCodeCopiedDesc"),
      });
    } catch {
      toast({
        title: t("checkout.pixCopyError"),
        description: t("checkout.pixCopyErrorDesc"),
        variant: "destructive",
      });
    }
  };

  const handlePixPayment = async () => {
    if (!slug) return;

    if (!document) {
      toast({
        title: t("checkout.requiredField"),
        description: t("checkout.fillDocumentField"),
        variant: "destructive",
      });
      return;
    }

    if (!validateDocument(document, documentType)) {
      toast({
        title: t("checkout.invalidDocument"),
        description: t("checkout.invalidDocumentDesc", { type: documentType.toUpperCase() }),
        variant: "destructive",
      });
      return;
    }

    const resolvedPhone = resolveCoursePurchasePhone({ country: phoneCountry, phone });
    if (!resolvedPhone) {
      toast({
        title: t("checkout.requiredField"),
        description: t("checkout.invalidPhoneDesc"),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: pixData } = await createCoursePurchasePix({
        variables: {
          slug,
          documentType,
          documentNumber: document.replace(/\D/g, ""),
          phone: resolvedPhone.legacyPhone,
          phoneDetails: resolvedPhone.phoneDetails,
          couponCode: appliedCoupon?.code,
        },
      });

      const order = pixData?.createCoursePurchasePix;
      if (order?.pix_qr_code_url && order?.pix_code && order?.expires_at && order.purchase_id) {
        const value = order.amount > 0 ? order.amount : (course?.price ?? 0);
        trackViralizeCheckout({
          transactionId: order.purchase_id,
          value,
          paymentMethod: "pix",
        });
        setPixOrder({
          purchase_id: order.purchase_id,
          pix_code: order.pix_code,
          pix_qr_code_url: order.pix_qr_code_url,
          expires_at: order.expires_at,
          value,
        });
        startPolling(order.purchase_id, value);
      } else {
        throw new Error("Incomplete PIX order response");
      }
    } catch (error) {
      console.error("Course PIX checkout error:", error);
      handleMutationError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardPayment = async () => {
    if (!slug) return;
    const resolvedPhone = resolveCoursePurchasePhone({ country: phoneCountry, phone });
    if (!resolvedPhone) {
      toast({
        title: t("checkout.requiredField"),
        description: t("checkout.invalidPhoneDesc"),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: checkoutData } = await createCoursePurchaseCheckout({
        variables: {
          slug,
          phone: resolvedPhone.legacyPhone,
          phoneDetails: resolvedPhone.phoneDetails,
          returnUrl: `${window.location.origin}/courses/${slug}?purchase=success`,
          cancelUrl: window.location.href,
          couponCode: appliedCoupon?.code,
        },
      });

      const checkout = checkoutData?.createCoursePurchaseCheckout;
      const url = checkout?.url;
      const purchaseId = checkout?.purchase_id;
      if (url && purchaseId) {
        const value = checkout.amount > 0 ? checkout.amount : appliedCoupon?.amount ?? course?.price ?? 0;
        trackViralizeCheckout({
          transactionId: purchaseId,
          value,
          paymentMethod: "card",
        });
        rememberViralizePurchase({
          transactionId: purchaseId,
          value,
        });
        window.location.href = url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Course card checkout error:", error);
      handleMutationError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatExpirationTime = (expiresAt: string) => {
    const countdown = formatPixCountdown(expiresAt, currentTime);
    if (countdown.expired) {
      return t("checkout.expired");
    }
    const parts: string[] = [];
    if (countdown.days > 0) parts.push(`${countdown.days}d`);
    if (countdown.hours > 0) parts.push(`${countdown.hours}h`);
    if (countdown.minutes > 0) parts.push(`${countdown.minutes}m`);
    if (countdown.seconds > 0 || parts.length === 0) parts.push(`${countdown.seconds}s`);
    return t("checkout.expiresIn", { time: parts.join(" ") });
  };

  if (courseLoading || !slug) {
    return (
      <Layout>
        <div className="flex-1 p-4 lg:p-6">
          <div className="container mx-auto text-center text-muted-foreground py-12">
            {t("common.loading")}
          </div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="flex-1 p-4 lg:p-6">
          <div className="container mx-auto max-w-2xl text-center py-12 space-y-4">
            <p className="text-lg font-semibold">{t("course.notFound")}</p>
            <Button variant="outline" onClick={() => navigate("/tutorials")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("course.back")}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Wait for both queries before deciding whether to redirect away — a
  // premature redirect on an unresolved subscription would bounce a paying
  // user before we know they're actually locked out or already own it.
  const myDataReady = !myDataLoading && !!myData;
  if (!myDataReady) {
    return (
      <Layout>
        <div className="flex-1 p-4 lg:p-6">
          <div className="container mx-auto text-center text-muted-foreground py-12">
            {t("common.loading")}
          </div>
        </div>
      </Layout>
    );
  }

  const isAdmin = myData?.myData?.is_admin === true;
  const purchaseCta = deriveCoursePurchaseState({
    isPurchasable: course.isPurchasable,
    price: course.price,
    ownedByViewer: course.ownedByViewer,
    isAdmin,
  });

  if (course.ownedByViewer || purchaseCta.kind === "hidden") {
    return <Navigate to={`/courses/${slug}`} replace />;
  }

  const catalogPrice = purchaseCta.kind === "buy" ? purchaseCta.price : 0;
  const price = appliedCoupon?.amount ?? catalogPrice;

  return (
    <Layout>
      <div className="flex-1 p-4 lg:p-6">
        <div className="max-w-2xl mx-auto space-y-6">
          <Button variant="ghost" onClick={() => navigate(`/courses/${slug}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("coursePurchase.backToCourse")}
          </Button>

          <Card>
            <CardHeader className="space-y-2">
              <CardTitle className="text-2xl">{t("coursePurchase.title")}</CardTitle>
              <p className="text-sm text-muted-foreground">{t("coursePurchase.subtitle")}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="space-y-1">
                  {appliedCoupon && catalogPrice > 0 && (
                    <p className="text-sm text-muted-foreground line-through">
                      {formatCoursePrice(catalogPrice)}
                    </p>
                  )}
                  <p className="text-3xl font-bold text-primary">{formatCoursePrice(price)}</p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">{t("coursePurchase.includedTitle")}</h4>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{t("coursePurchase.includedCourse")}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{t("coursePurchase.includedPlan")}</span>
                  </div>
                </div>
              </div>

              {!pixOrder && (
                <Tabs
                  value={paymentMethod}
                  onValueChange={(value) => setPaymentMethod(value as "pix" | "credit_card")}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="credit_card">
                      <CreditCard className="w-4 h-4 mr-2" />
                      {t("coursePurchase.payWithCard")}
                    </TabsTrigger>
                    <TabsTrigger value="pix">
                      <QrCode className="w-4 h-4 mr-2" />
                      {t("coursePurchase.payWithPix")}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              {!pixOrder && (
                <div className="space-y-3 pt-2 border-t">
                  <h4 className="font-semibold text-sm">{t("checkout.discountCode")}</h4>
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-950">
                      <div>
                        <p className="font-semibold text-green-700 dark:text-green-300">
                          {t("checkout.couponApplied", { code: appliedCoupon.code })}
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          {t("checkout.discount", { percent: appliedCoupon.discount })}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setAppliedCoupon(null);
                          setCouponCode("");
                        }}
                      >
                        {t("checkout.remove")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("checkout.enterCouponCode")}
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void applyCoupon(couponCode);
                          }
                        }}
                        disabled={couponValidating || submitting}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => void applyCoupon(couponCode)}
                        disabled={couponValidating || submitting || !couponCode.trim()}
                      >
                        {couponValidating ? t("checkout.validating") : t("checkout.apply")}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {!pixOrder && (
                <CoursePurchasePhoneField
                  country={phoneCountry}
                  phone={phone}
                  onCountryChange={(country) => {
                    setPhoneDraft((current) => ({ ...current, country }));
                  }}
                  onPhoneChange={(nextPhone) => {
                    setPhoneDraft((current) => ({ ...current, phone: nextPhone }));
                  }}
                  disabled={submitting}
                />
              )}

              {!pixOrder && paymentMethod === "pix" && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="documentType">{t("checkout.documentType")}</Label>
                    <Select
                      value={documentType}
                      onValueChange={(value: "cpf" | "cnpj") => {
                        setDocumentType(value);
                        setDocument("");
                      }}
                    >
                      <SelectTrigger id="documentType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cpf">CPF</SelectItem>
                        <SelectItem value="cnpj">CNPJ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="document">{documentType === "cpf" ? "CPF" : "CNPJ"}</Label>
                    <Input
                      id="document"
                      value={document}
                      onChange={(e) => handleDocumentChange(e.target.value)}
                      placeholder={documentType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                      maxLength={documentType === "cpf" ? 14 : 18}
                      disabled={submitting}
                    />
                  </div>
                  <Button
                    onClick={handlePixPayment}
                    variant="gradient"
                    disabled={submitting}
                    size="lg"
                    className="w-full"
                  >
                    {submitting ? t("checkout.processing") : t("coursePurchase.payWithPix")}
                  </Button>
                </div>
              )}

              {!pixOrder && paymentMethod === "credit_card" && (
                <Button
                  onClick={handleCardPayment}
                  variant="gradient"
                  disabled={submitting}
                  size="lg"
                  className="w-full"
                >
                  {submitting ? t("checkout.processing") : t("coursePurchase.payWithCard")}
                </Button>
              )}

              {pixOrder && (
                <div className="space-y-6 text-center">
                  <div className="flex items-center justify-center gap-2 text-amber-600">
                    <Clock className="w-5 h-5 animate-pulse" />
                    <p className="text-lg font-semibold">{t("checkout.awaitingPayment")}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{t("checkout.scanQrCode")}</p>

                  <figure className="flex flex-col items-center gap-3">
                    <div className="bg-white p-4 rounded-lg shadow-lg">
                      <img src={pixOrder.pix_qr_code_url} alt="QR Code PIX" className="w-64 h-64" />
                    </div>
                    <figcaption>
                      <Badge
                        variant="outline"
                        className={(() => {
                          const { totalMinutes } = formatPixCountdown(pixOrder.expires_at, currentTime);
                          if (totalMinutes <= 2) {
                            return "bg-red-50 text-red-700 border-red-300 animate-pulse";
                          } else if (totalMinutes <= 5) {
                            return "bg-orange-50 text-orange-700 border-orange-300";
                          }
                          return "bg-amber-50 text-amber-700 border-amber-300";
                        })()}
                      >
                        {formatExpirationTime(pixOrder.expires_at)}
                      </Badge>
                    </figcaption>
                  </figure>

                  <div className="space-y-2">
                    <p className="text-sm font-medium">{t("checkout.orCopyPixCode")}</p>
                    <div className="flex items-center gap-2 max-w-md mx-auto">
                      <Input value={pixOrder.pix_code} readOnly className="text-xs font-mono" />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyPixCode(pixOrder.pix_code)}
                        title={t("checkout.pixCodeCopied")}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      clearAllIntervals();
                      setPixOrder(null);
                    }}
                  >
                    {t("checkout.generateNewQrCode")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

const CourseCheckout = () => {
  const { t } = useTranslation();
  // Store rules forbid web payment inside the mobile app.
  if (currentEmbeddedApp()) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <p>{t("billing.embeddedCourseCheckout")}</p>
      </div>
    );
  }
  return <CourseCheckoutContent />;
};

export default CourseCheckout;
